import React from 'react';
import {useSearchParams} from 'wouter';

type SetValue<ParamValue> = (paramValue: ParamValue) => void;

/** URL search parameters used in the app */
export enum UrlSearchParam {
    /** Represents a version of documents */
    GV = 'GV',
    /** Contains search data for explore view in base64 */
    search = 'search',
    /** Used to redirect user after login */
    originUrl = 'originUrl'
}

interface UseUrlSearchValue<ParamValue> {
    value: string | null;
    setValue: SetValue<ParamValue>;
    replaceValue: SetValue<ParamValue>;
    removeValue: () => void;
}

export const addGlobalVersionToUrl = (basePath: string, GV: number | null) => {
    return GV ? `${basePath}?${UrlSearchParam.GV}=${GV}` : basePath;
};

export const useUrlSearchValue = <P extends UrlSearchParam, V extends string>(
    paramName: P
): UseUrlSearchValue<V> => {
    const [searchParams, setSearchParams] = useSearchParams();

    const setValue = React.useCallback(
        (paramValue: V) => {
            setSearchParams((prevSearchParams: URLSearchParams) => {
                const nextSearchParams = new URLSearchParams(prevSearchParams);

                nextSearchParams.set(paramName, paramValue);

                return nextSearchParams;
            });
        },
        [paramName, setSearchParams]
    );

    const replaceValue = React.useCallback(
        (paramValue: V) => {
            setSearchParams(
                (prevSearchParams: URLSearchParams) => {
                    const nextSearchParams = new URLSearchParams(prevSearchParams);

                    nextSearchParams.set(paramName, paramValue);

                    return nextSearchParams;
                },
                {
                    replace: true
                }
            );
        },
        [paramName, setSearchParams]
    );

    const removeValue = React.useCallback(() => {
        setSearchParams((prevSearchParams: URLSearchParams) => {
            const nextSearchParams = new URLSearchParams(prevSearchParams);

            nextSearchParams.delete(paramName);

            return nextSearchParams;
        });
    }, [paramName, setSearchParams]);

    return {
        value: searchParams.get(paramName),
        setValue,
        replaceValue,
        removeValue
    };
};
